import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Check, CheckCheck, X, Clock, Briefcase, Palette, Share2, ClipboardCheck, AlertCircle, Lock, Loader2, Wifi, WifiOff } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NOTIFICATIONS_PER_PAGE = 15;

const NotificationBell = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isConnected, newNotificationCount, resetNotificationCount } = useWebSocket();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const [accessErrorType, setAccessErrorType] = useState(null); // 'deleted' | 'denied' | 'error'
  const [verifyingId, setVerifyingId] = useState(null);
  const dropdownRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const token = localStorage.getItem('token');
  const isReadOnly = user?.role_level === 'Read-only';

  const fetchNotifications = useCallback(async (reset = true) => {
    if (!token) return;
    
    try {
      const skip = reset ? 0 : notifications.length;
      const response = await axios.get(`${API}/notifications?limit=${NOTIFICATIONS_PER_PAGE}&skip=${skip}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newNotifications = response.data.notifications || [];
      const totalCount = response.data.total || 0;
      
      if (reset) {
        setNotifications(newNotifications);
      } else {
        setNotifications(prev => [...prev, ...newNotifications]);
      }
      
      setUnreadCount(response.data.unread_count || 0);
      setTotal(totalCount);
      setHasMore(skip + newNotifications.length < totalCount);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [token, notifications.length]);

  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [token]);

  // Initial fetch and polling setup
  useEffect(() => {
    fetchUnreadCount();
    
    // Poll for unread count every 60 seconds (reduced frequency since we have WebSocket)
    // This serves as a backup in case WebSocket is disconnected
    pollIntervalRef.current = setInterval(fetchUnreadCount, 60000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchUnreadCount]);

  // Listen for real-time notifications via WebSocket
  useEffect(() => {
    const handleNewNotification = (event) => {
      const { type, data } = event.detail;
      
      // Add new notification to the top of the list
      setNotifications(prev => {
        // Avoid duplicates
        if (prev.some(n => n.id === data.id)) {
          return prev;
        }
        return [data, ...prev];
      });
      
      // Increment unread count
      setUnreadCount(prev => prev + 1);
      setTotal(prev => prev + 1);
    };

    window.addEventListener('new-notification', handleNewNotification);
    
    return () => {
      window.removeEventListener('new-notification', handleNewNotification);
    };
  }, []);

  // Reset WebSocket notification count when dropdown is opened
  useEffect(() => {
    if (isOpen) {
      resetNotificationCount();
    }
  }, [isOpen, resetNotificationCount]);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      fetchNotifications(true).finally(() => setLoading(false));
    }
  }, [isOpen]);

  // Load more notifications handler
  const loadMoreNotifications = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    await fetchNotifications(false);
    setLoadingMore(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(
        `${API}/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(
        `${API}/notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const verifyingRef = useRef(null);

  const handleNotificationClick = async (notification) => {
    // Avoid duplicate/overlapping access-verification requests if the user
    // clicks another notification (or the same one) while one is in flight.
    // Set the guard synchronously via a ref BEFORE any `await` — state
    // updates from setVerifyingId don't apply until the next render, so a
    // second click fired during the `markAsRead` await would otherwise
    // still read the old (empty) `verifyingId` and slip through.
    if (verifyingRef.current) return;
    verifyingRef.current = notification.id;
    setVerifyingId(notification.id);

    // Clear any previous access error
    setAccessError(null);
    setAccessErrorType(null);
    
    try {
      // Mark as read first
      if (!notification.is_read) {
        await markAsRead(notification.id);
      }
      
      // If no reference, just close (informational notification)
      if (!notification.reference_id || !notification.reference_type) {
        setIsOpen(false);
        return;
      }
      
      // Validate access before navigation (RBAC enforcement)
      const response = await axios.get(
        `${API}/notifications/${notification.id}/validate-access`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { has_access, reference_exists, reference_type, message } = response.data;
      
      if (!reference_exists) {
        setAccessErrorType('deleted');
        setAccessError(message || 'This item no longer exists.');
        setTimeout(() => setAccessError(null), 4000);
        return;
      }

      if (!has_access) {
        // Genuinely denied by RBAC — show a clear permission message, do not navigate.
        setAccessErrorType('denied');
        setAccessError(message || "You don't have permission to view this item.");
        setTimeout(() => setAccessError(null), 4000);
        return;
      }
      
      // Navigate based on reference type — destination pages read the
      // `highlight` param and open the matching Task/Post Details modal.
      if (reference_type === 'task') {
        navigate(`/tasks?highlight=${notification.reference_id}`);
      } else if (reference_type === 'social_post') {
        navigate(`/social-media/posts?highlight=${notification.reference_id}`);
      }
      
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to validate notification access:', error);
      setAccessErrorType('error');
      setAccessError('Failed to verify access. Please try again.');
      setTimeout(() => setAccessError(null), 4000);
    } finally {
      verifyingRef.current = null;
      setVerifyingId(null);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'task':
        return <Briefcase size={16} className="text-blue-500" />;
      case 'uiux':
        return <Palette size={16} className="text-purple-500" />;
      case 'social':
        return <Share2 size={16} className="text-pink-500" />;
      case 'approval':
        return <ClipboardCheck size={16} className="text-amber-500" />;
      default:
        return <Bell size={16} className="text-slate-400" />;
    }
  };

  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return 'recently';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell size={22} className="text-slate-600" />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        
        {/* WebSocket Connection Indicator */}
        <span
          className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${
            isConnected ? 'bg-green-500' : 'bg-slate-400'
          }`}
          title={isConnected ? 'Real-time connected' : 'Connecting...'}
        />
      </button>

      {/* Dropdown Panel - Using fixed positioning to avoid overflow clipping */}
      {isOpen && (
        <div
          className="fixed top-auto left-auto mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-slate-200 z-[100] overflow-hidden"
          style={{
            top: dropdownRef.current ? dropdownRef.current.getBoundingClientRect().bottom + 8 : 'auto',
            left: dropdownRef.current 
              ? Math.max(16, Math.min(dropdownRef.current.getBoundingClientRect().left, window.innerWidth - 400)) 
              : 'auto'
          }}
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center gap-2">
              <Bell size={18} className="text-slate-600" />
              <h3 className="font-semibold text-slate-800">Notifications</h3>
              {unreadCount > 0 && (
                <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full whitespace-nowrap">
                  {unreadCount} new
                </span>
              )}
              {/* Real-time status indicator */}
              <span 
                className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                  isConnected 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-slate-200 text-slate-600'
                }`}
                title={isConnected ? 'Real-time notifications active' : 'Connecting to real-time...'}
              >
                {isConnected ? <Wifi size={10} /> : <WifiOff size={10} />}
                <span className="hidden sm:inline">{isConnected ? 'Live' : 'Offline'}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 whitespace-nowrap flex-shrink-0"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck size={14} />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="max-h-[400px] overflow-y-auto">
            {/* Access Error Alert */}
            {accessError && (
              <div className="mx-4 mt-3 mb-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="access-error-alert">
                <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-700">
                    {accessErrorType === 'deleted' ? 'Item Unavailable' : accessErrorType === 'error' ? 'Something Went Wrong' : 'Access Denied'}
                  </p>
                  <p className="text-xs text-red-600">{accessError}</p>
                </div>
              </div>
            )}
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400" data-testid="empty-notifications">
                <Bell size={40} className="mb-3 opacity-50" />
                <p className="text-sm font-medium">No notifications in the last 30 days.</p>
                <p className="text-xs mt-1">You're all caught up!</p>
              </div>
            ) : (
              <div>
                {notifications.map((notification) => {
                  const isVerifying = verifyingId === notification.id;
                  return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-4 py-3 border-b border-slate-50 transition-colors hover:bg-slate-50 ${
                      !notification.is_read ? 'bg-blue-50/50' : ''
                    } ${verifyingId && !isVerifying ? 'opacity-60 pointer-events-none' : 'cursor-pointer'}`}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                        !notification.is_read ? 'bg-white shadow-sm' : 'bg-slate-100'
                      }`}>
                        {isVerifying ? <Loader2 size={16} className="text-cyan-600 animate-spin" /> : getNotificationIcon(notification.type)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            {/* Actor name - bold and highlighted */}
                            {notification.actor_name && (
                              <p className="text-sm font-bold text-cyan-700 mb-0.5">
                                {notification.actor_name}
                              </p>
                            )}
                            {/* Title */}
                            <p
                              className={`text-sm ${!notification.is_read ? 'font-semibold text-slate-800' : 'font-medium text-slate-600'} ${notification.reference_id ? 'hover:underline' : ''}`}
                              data-testid={`notification-title-${notification.id}`}
                            >
                              {notification.title}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {isReadOnly && notification.reference_id && (
                              <Lock size={12} className="text-slate-400" title="View only" />
                            )}
                            {!notification.is_read && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-1 mt-1.5 text-xs text-slate-400">
                          <Clock size={12} />
                          <span>{formatTime(notification.created_at)}</span>
                          {isVerifying && <span className="text-cyan-600 ml-1">Opening…</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer with Load More */}
          {(hasMore || notifications.length > 0) && (
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
              {hasMore ? (
                <button
                  onClick={loadMoreNotifications}
                  disabled={loadingMore}
                  className="w-full py-2 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="load-more-notifications-btn"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>Load More ({total - notifications.length} remaining)</>
                  )}
                </button>
              ) : (
                <p className="text-xs text-slate-500 text-center">
                  Showing all notifications from the last 30 days ({notifications.length})
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
